import * as Highcharts from "highcharts";
import * as React from "react";
import HighchartsReact from "highcharts-react-official";
import { ITopicsChartData, TopicsChartFilter } from "../../../../App/Analytics/Common";
import {
  centerSeriesBarTooltip,
  defaultStyle,
  defaultSmallAxisLabels,
  defaultAxisLabels,
  defaultTooltip,
} from "../../../../utils/charts";
import colors from "../../../../styles/colors";
import { createGradient, lessonsTooltipFormatter } from "../../../../formatters/analytics";

export interface ITotalLearnersData {
  totalLearners: number;
  totalFilteredLearners: number;
}

interface ILessonsChartProps {
  data: ITopicsChartData[];
  filterFunction: TopicsChartFilter;
  ticks: number[];
  totalLearnersData: ITotalLearnersData;
  isFilterActive: boolean;
}

const options = (
  data: ITopicsChartData[],
  filteredFn: TopicsChartFilter,
  totalLearnersData: ITotalLearnersData,
  isFilterActive: boolean
): Highcharts.Options => {
  return {
    chart: {
      type: "column",
    },
    plotOptions: {
      column: {
        borderRadius: 5,
      },
      series: {
        dataLabels: {
          style: {
            fontSize: "1.4rem",
          },
        },
        events: {
          click: event => {
            const topics = Number(event.point.category);
            return filteredFn(topics);
          },
        },
      },
    },
    series: [
      {
        data: data.map((d: ITopicsChartData) => d.students),
        name: "Learners",
        /* tslint:disable-next-line */
        color: isFilterActive
          ? createGradient(colors.chartFilteredGray100, colors.chartFilteredGray70)
          : createGradient(colors.chartGreen100, colors.chartGreen70),
      } as Highcharts.SeriesBarOptions,
      isFilterActive &&
        ({
          data: data.map((d: ITopicsChartData) => d.filtered ?? 0),
          name: "Filtered",
          /* tslint:disable-next-line */
          color: createGradient(colors.chartGreen100, colors.chartGreen70),
        } as Highcharts.SeriesBarOptions),
    ].filter(Boolean) as Highcharts.SeriesBarOptions[],
    title: {
      style: {
        fontSize: "1.5rem",
      },
      text: undefined,
    },
    tooltip: {
      shape: "square",
      formatter() {
        const label = this.x!;
        const value = this.y!;
        const isFiltered = isFilterActive && this.series.name === "Filtered";
        const maxstudents =
          this.series.name === "Learners" ? totalLearnersData.totalLearners : totalLearnersData.totalFilteredLearners;
        const textColor = this.series.color as Highcharts.GradientColorObject;
        return lessonsTooltipFormatter(value, maxstudents, label, textColor.stops[0][1], isFiltered);
      },
      positioner(...params) {
        return centerSeriesBarTooltip(this.chart)(...params);
      },
      ...defaultTooltip,
    },
    xAxis: {
      categories: data.map(({ topics }) => topics.toString()),
      title: {
        style: {
          fontSize: "1.45rem",
        },
        text: "Number of Lessons",
      },
      ...defaultSmallAxisLabels(),
    },
    yAxis: {
      min: 0,
      title: {
        style: {
          fontSize: "1.45rem",
        },
        text: "Learners",
      },
      ...defaultAxisLabels(),
    },
    ...defaultStyle,
  };
};

const TopicsChart: React.SFC<ILessonsChartProps> = ({ data, filterFunction, totalLearnersData, isFilterActive }) => {
  const chartComponentRef = React.useRef<HighchartsReact.RefObject>(null);
  return (
    <HighchartsReact
      containerProps={{ style: { width: "100%", height: "100%", display: "block" } }}
      highcharts={Highcharts}
      options={options(data, filterFunction, totalLearnersData, isFilterActive)}
      ref={chartComponentRef}
    />
  );
};

export default TopicsChart;
